import {
  Component,
  computed,
  EventEmitter,
  Injector,
  Output,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { INodeInspectorAdapter } from '@shared/components/joint-js/services/INodeInspectorAdapter';
import { NodeClass } from '@modules/campaigns/Domain/NodeClass';
import { NodeValidationError } from '@modules/campaigns/Domain/Interfaces/NodeValidationError';
import { ConditionalExperimentNode } from '@modules/campaigns/Domain/Nodes/ConditionalExperimentNode';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { NgIf, NgForOf, NgClass } from '@angular/common';
import { DividerModule } from 'primeng/divider';
import { PipeModule } from '@shared/pipes/pipe.module';
import { TranslateModule } from '@ngx-translate/core';
import { TabViewModule } from 'primeng/tabview';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormsModule } from '@angular/forms';
import { SliderModule } from 'primeng/slider';
import { InputTextModule } from 'primeng/inputtext';
import { Button } from 'primeng/button';
import { InspectorMode } from '@shared/components/joint-js/components/inspectors/node-inspector/node-inspector.component';

@Component({
  selector: 'app-experiment-inspector',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    DividerModule,
    PipeModule,
    TranslateModule,
    TabViewModule,
    NgClass,
    SelectButtonModule,
    FormsModule,
    SliderModule,
    InputTextModule,
    Button,
  ],
  templateUrl: './experiment-inspector.component.html',
  styleUrls: ['./experiment-inspector.component.scss'],
})
export class ExperimentInspectorComponent implements INodeInspectorAdapter {
  @Output() closeSidebar = new EventEmitter<void>();

  private percentageChangeTimeout?: ReturnType<typeof setTimeout>;

  node: WritableSignal<NodeClass | null> = signal<NodeClass | null>(null);

  activeTab = signal<'preview' | 'config'>('preview');
  mode = signal<'edit' | 'preview'>('edit');

  caseTrue = signal<string | null>(null);
  caseFalse = signal<string | null>(null);
  percentageA = signal<number>(50);
  percentageB = signal<number>(50);

  errors = signal<NodeValidationError[]>([]);

  presets = [
    { a: 50, label: '50/50' },
    { a: 80, label: '80/20' },
    { a: 70, label: '70/30' },
    { a: 90, label: '90/10' },
  ];

  tabs = [
    { label: 'Vista', value: 'preview' },
    { label: 'Configuración', value: 'config' },
  ];

  constructor(private workflow: WorkflowStateService) {}

  readonly visibleTabs = computed(() =>
    this.mode() === 'edit' ? this.tabs : this.tabs.filter((t) => t.value === 'preview'),
  );

  get nodeValue(): ConditionalExperimentNode | null {
    const n = this.node();
    if (!n) return null;
    return n.type === 'experiment' ? (n as ConditionalExperimentNode) : null;
  }

  private scheduleUpdate() {
    if (this.percentageChangeTimeout) clearTimeout(this.percentageChangeTimeout);
    this.percentageChangeTimeout = setTimeout(() => {
      this.applyChanges();
    }, 1000);
  }

  private refreshUI() {
    const n = this.nodeValue;
    if (!n) return;

    const cfg: any = n.config ?? {};
    this.caseTrue.set(cfg.cases?.true ?? null);
    this.caseFalse.set(cfg.cases?.false ?? null);
    this.percentageA.set(cfg.percentage ?? 50);
    this.percentageB.set(100 - (this.percentageA() ?? 0));
    this.errors.set([]);
  }

  applyChanges() {
    if (this.mode() === 'preview') return;

    const n = this.nodeValue;
    if (!n) return;

    const updated = n.cloneWithConfig({
      percentage: this.percentageA(),
    });

    const validation = updated.validate();
    this.errors.set(validation);

    if (validation.length === 0) {
      this.workflow.addOrUpdateNode(updated);
    }
  }

  setNode(nodeSignal: Signal<NodeClass | null>) {
    this.node.set(nodeSignal());
    this.refreshUI();
  }

  updatePercentageA(value: number) {
    if (isNaN(value)) value = 0;
    if (value < 0) value = 0;
    if (value > 100) value = 100;
    this.percentageA.set(value);
    this.percentageB.set(100 - value);
    this.scheduleUpdate();
  }

  updatePercentageB(value: number) {
    if (isNaN(value)) value = 0;
    if (value < 0) value = 0;
    if (value > 100) value = 100;
    this.percentageB.set(value);
    this.percentageA.set(100 - value);
    this.scheduleUpdate();
  }

  applyPreset(value: number) {
    this.percentageA.set(value);
    this.percentageB.set(100 - value);
    this.scheduleUpdate();
  }

  onSliderChange() {
    this.percentageB.set(100 - this.percentageA());
    this.scheduleUpdate();
  }

  setActiveTab(tab: 'preview' | 'config') {
    this.activeTab.set(tab);
  }

  setMode(mode: InspectorMode) {
    this.mode.set(mode);
    this.activeTab.set(mode === 'edit' ? 'config' : 'preview');
  }
}
